import React, { useState, useEffect } from 'react';
import TaskBar from './TaskBar';
import { loadTasks, saveTasks, generateTaskId } from './utils';
import './ContextSwitcher.css';

function ContextSwitcher() {
  const [tasks, setTasks] = useState(loadTasks);
  const [statusUpdateText, setStatusUpdateText] = useState('');
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedFrom, setDraggedFrom] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedOver, setDraggedOver] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showTodoColumn, setShowTodoColumn] = useState(true);
  const [showRightColumn, setShowRightColumn] = useState(true);
  const [isTitleBarCollapsed, setIsTitleBarCollapsed] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const updateTaskTimestamp = (task) => {
    return { ...task, lastWorkedOn: Date.now() };
  };

  useEffect(() => {
    saveTasks(tasks);
  }, [tasks]);

  const handleStatusUpdate = (e) => {
    e.preventDefault();
    const inputText = statusUpdateText.trim();
    
    if (!inputText) return;
    
    // Check for command mode
    if (inputText.startsWith('/help')) {
      setShowHelpModal(true);
      setStatusUpdateText('');
      return;
    }
    
    if (inputText.startsWith('/done')) {
      if (tasks.active.length > 0) {
        const newActive = [...tasks.active];
        const currentTask = updateTaskTimestamp({ ...newActive[0] });
        newActive.splice(0, 1);
        setTasks(prev => ({
          ...prev,
          active: newActive,
          completed: [...prev.completed, currentTask]
        }));
        setStatusUpdateText('');
      }
      return;
    }
    
    if (inputText.startsWith('/discard')) {
      if (tasks.active.length > 0) {
        const newActive = [...tasks.active];
        const currentTask = updateTaskTimestamp({ ...newActive[0] });
        newActive.splice(0, 1);
        setTasks(prev => ({
          ...prev,
          active: newActive,
          discard: [...prev.discard, currentTask]
        }));
        setStatusUpdateText('');
      }
      return;
    }
    
    if (inputText.startsWith('/bar')) {
      setIsTitleBarCollapsed(!isTitleBarCollapsed);
      setStatusUpdateText('');
      return;
    }
    
    if (inputText.startsWith('/left')) {
      setShowTodoColumn(!showTodoColumn);
      setStatusUpdateText('');
      return;
    }
    
    if (inputText.startsWith('/right')) {
      setShowRightColumn(!showRightColumn);
      setStatusUpdateText('');
      return;
    }
    
    if (inputText.startsWith('/todo')) {
      const taskText = inputText.substring(5).trim();
      if (taskText) {
        // Capitalize first letter
        const capitalizedText = taskText.charAt(0).toUpperCase() + taskText.slice(1);
        const newTask = {
          id: generateTaskId(),
          text: capitalizedText,
          status: ''
        };
        setTasks(prev => ({
          ...prev,
          todo: [...prev.todo, newTask]
        }));
        setStatusUpdateText('');
      }
      return;
    }
    
    // Normal status update
    if (tasks.active.length > 0) {
      const newActive = [...tasks.active];
      const ongoingTask = updateTaskTimestamp({ ...newActive[0] });
      ongoingTask.status = inputText;
      
      // Remove from top and add to bottom
      newActive.splice(0, 1);
      newActive.push(ongoingTask);
      
      setIsAnimating(true);
      setTasks(prev => ({
        ...prev,
        active: newActive
      }));
      setStatusUpdateText('');
      
      // Reset animation flag after animation completes
      setTimeout(() => setIsAnimating(false), 400);
    }
  };

  const handleDragStart = (e, task, column, index) => {
    setDraggedTask(task);
    setDraggedFrom(column);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    setTimeout(() => {
      e.target.style.opacity = '0.5';
    }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedTask(null);
    setDraggedFrom(null);
    setDraggedIndex(null);
    setDraggedOver(null);
    setDropIndex(null);
  };

  const handleDragOver = (e, column) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOver(column);
    
    // For active column, calculate drop index
    if (column === 'active' && draggedFrom === 'active') {
      const dropZone = e.currentTarget;
      const taskBars = Array.from(dropZone.querySelectorAll('.task-bar'));
      const zoneRect = dropZone.getBoundingClientRect();
      const y = e.clientY - zoneRect.top;
      
      let index = taskBars.length;
      for (let i = 0; i < taskBars.length; i++) {
        if (i === draggedIndex) continue; // Skip the dragged item
        const barRect = taskBars[i].getBoundingClientRect();
        const barTop = barRect.top - zoneRect.top;
        const barHeight = barRect.height;
        if (y < barTop + barHeight / 2) {
          index = i;
          break;
        }
      }
      // If we didn't find a position, use the end (accounting for dragged item)
      if (index === taskBars.length && draggedIndex < taskBars.length - 1) {
        index = taskBars.length - 1;
      }
      setDropIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDraggedOver(null);
    setDropIndex(null);
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    setDraggedOver(null);
    setDropIndex(null);

    if (!draggedTask || !draggedFrom) return;

    // Prevent reordering within done column - only allow moving FROM done to other columns
    if (draggedFrom === targetColumn && targetColumn === 'completed') {
      return;
    }

    // Prevent reordering within active column by drag (only allow click)
    if (draggedFrom === targetColumn && targetColumn === 'active') {
      // Reordering within active column - update timestamp
      const newActive = [...tasks.active];
      const [removed] = newActive.splice(draggedIndex, 1);
      const updatedRemoved = updateTaskTimestamp(removed);
      
      // Insert at drop position
      let insertIndex = dropIndex !== null ? dropIndex : draggedIndex;
      if (insertIndex > draggedIndex) {
        insertIndex--; // Adjust for removed item
      }
      newActive.splice(insertIndex, 0, updatedRemoved);
      
      setTasks(prev => ({ ...prev, active: newActive }));
      return;
    }

    // Moving between columns - update timestamp
    const sourceArray = [...tasks[draggedFrom]];
    const targetArray = [...tasks[targetColumn]];

    sourceArray.splice(draggedIndex, 1);
    const updatedTask = updateTaskTimestamp(draggedTask);
    
    // For active column, always add to top; for others, add to end
    if (targetColumn === 'active') {
      targetArray.unshift(updatedTask);
    } else {
      targetArray.push(updatedTask);
    }

    setTasks(prev => ({
      ...prev,
      [draggedFrom]: sourceArray,
      [targetColumn]: targetArray
    }));
  };

  const handleTaskClick = (column, index) => {
    if (column !== 'active') return;
    // Don't allow clicking on done or discard columns

    const newActive = [...tasks.active];
    if (index === 0) {
      // Click top bar - push to bottom with animation
      setIsAnimating(true);
      const [top] = newActive.splice(0, 1);
      const updatedTop = updateTaskTimestamp(top);
      newActive.push(updatedTop);
      setTasks(prev => ({ ...prev, active: newActive }));
      
      // Reset animation flag after animation completes
      setTimeout(() => setIsAnimating(false), 400);
    } else {
      // Click other bar - push to top
      const [clicked] = newActive.splice(index, 1);
      const updatedClicked = updateTaskTimestamp(clicked);
      newActive.unshift(updatedClicked);
      setTasks(prev => ({ ...prev, active: newActive }));
    }
  };

  const renderTaskBar = (task, column, index) => {
    const isDragging = draggedTask?.id === task.id && draggedFrom === column;
    const isOngoingTask = column === 'active' && index === 0;
    const isDoneColumn = column === 'completed';
    const isDiscardColumn = column === 'discard';
    
    return (
      <TaskBar
        key={task.id}
        task={task}
        onDragStart={(e) => handleDragStart(e, task, column, index)}
        onDragEnd={handleDragEnd}
        onClick={isDoneColumn || isDiscardColumn ? undefined : () => handleTaskClick(column, index)}
        isDragging={isDragging}
        isOngoingTask={isOngoingTask}
      />
    );
  };

  return (
    <div className="context-switcher">
      <div className={`title-bar ${isTitleBarCollapsed ? 'collapsed' : ''}`}>
        <div className="title-bar-title">Context Switcher</div>
      </div>
      <div className="context-switcher-container">
        {/* Left Column */}
        {showTodoColumn && (
          <div className="column left-column">
            {/* Todo Pile */}
            <div className="pile todo-pile">
              <div className="pile-label">Todo</div>
              <div
                className={`drop-zone ${draggedOver === 'todo' ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, 'todo')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 'todo')}
              >
                {tasks.todo.map((task, index) => renderTaskBar(task, 'todo', index))}
              </div>
            </div>
          </div>
        )}

        {/* Middle Column - Active Tasks */}
        <div className="column middle-column">
          <div className="pile active-pile">
            <div className="pile-label">Active</div>
            <div
              className={`drop-zone active-drop-zone ${draggedOver === 'active' ? 'drag-over' : ''} ${isAnimating ? 'animating' : ''}`}
              onDragOver={(e) => handleDragOver(e, 'active')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'active')}
            >
              {tasks.active.map((task, index) => renderTaskBar(task, 'active', index))}
            </div>
            <form onSubmit={handleStatusUpdate} className="status-update-form">
              <input
                type="text"
                value={statusUpdateText}
                onChange={(e) => setStatusUpdateText(e.target.value)}
                placeholder="Update status or /help..."
                className="status-input"
              />
            </form>
          </div>
        </div>

        {/* Right Column - Completed and Discard */}
        {showRightColumn && (
          <div className="column right-column">
            <div className="pile completed-pile">
              <div className="pile-label">Done</div>
              <div
                className={`drop-zone ${draggedOver === 'completed' ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, 'completed')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 'completed')}
              >
                {[...tasks.completed]
                  .sort((a, b) => {
                    const timeA = a.lastWorkedOn || 0;
                    const timeB = b.lastWorkedOn || 0;
                    return timeB - timeA; // Descending order (most recent first)
                  })
                  .map((task, index) => renderTaskBar(task, 'completed', index))}
              </div>
            </div>

            {/* Discard Pile */}
            <div className="pile discard-pile">
              <div className="pile-label">Discard</div>
              <div
                className={`drop-zone ${draggedOver === 'discard' ? 'drag-over' : ''}`}
                onDragOver={(e) => handleDragOver(e, 'discard')}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, 'discard')}
              >
                {tasks.discard.map((task, index) => renderTaskBar(task, 'discard', index))}
              </div>
            </div>
          </div>
        )}
      </div>
      {showHelpModal && (
        <div className="help-modal-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>Available Commands</h2>
              <button className="help-modal-close" onClick={() => setShowHelpModal(false)}>
                ×
              </button>
            </div>
            <div className="help-modal-content">
              <div className="help-command">
                <code>/todo &lt;task name&gt;</code>
                <span>Add a new task to the Todo pile</span>
              </div>
              <div className="help-command">
                <code>/done</code>
                <span>Move the current task to Done pile</span>
              </div>
              <div className="help-command">
                <code>/discard</code>
                <span>Move the current task to Discard pile</span>
              </div>
              <div className="help-command">
                <code>/bar</code>
                <span>Toggle the title bar visibility</span>
              </div>
              <div className="help-command">
                <code>/left</code>
                <span>Toggle the Todo column visibility</span>
              </div>
              <div className="help-command">
                <code>/right</code>
                <span>Toggle the Right column (Done/Discard) visibility</span>
              </div>
              <div className="help-command">
                <code>/help</code>
                <span>Show this help dialog</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContextSwitcher;

